// src/components/DashboardPage.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/DashboardPage.css";
import MenuLayout from "./MenuLayout";
import { getAccountChallenges } from "../api/challenge";
import { getEconomicCalendar } from "../api/features";

const WP_NEWS_URL = "https://touchprop.com/wp-json/wp/v2/posts?_embed&per_page=6";
const CALENDAR_CACHE_KEY = "touchprop:economic-calendar:v2";
const CALENDAR_BROWSER_CACHE_MS = 6 * 60 * 60 * 1000;

const stripHtml = (html) => {
  if (!html) return "";
  const text = html.replace(/<[^>]+>/g, " ");
  return text.replace(/\s+/g, " ").trim();
};

const readCalendarCache = () => {
  try {
    const cached = JSON.parse(sessionStorage.getItem(CALENDAR_CACHE_KEY));
    if (!cached?.payload || !Array.isArray(cached.payload.events)) return null;
    if (Date.now() - Number(cached.cachedAt || 0) > CALENDAR_BROWSER_CACHE_MS) {
      sessionStorage.removeItem(CALENDAR_CACHE_KEY);
      return null;
    }
    return cached.payload;
  } catch {
    return null;
  }
};

const writeCalendarCache = (payload) => {
  try {
    sessionStorage.setItem(
      CALENDAR_CACHE_KEY,
      JSON.stringify({ payload, cachedAt: Date.now() })
    );
  } catch {
    // The calendar still works when storage is disabled by the browser.
  }
};

const normalizeCalendarPayload = (data) => {
  if (Array.isArray(data)) {
    return { events: data, impact_filter: "high", stale: false };
  }
  return {
    ...data,
    events: Array.isArray(data?.events) ? data.events : [],
    impact_filter: data?.impact_filter || "high",
  };
};

const getEventDate = (event) => {
  const rawTimestamp = Number(event?.timestamp);
  const rawDate = event?.datetime || event?.date;
  const parsed = rawDate
    ? new Date(rawDate)
    : Number.isFinite(rawTimestamp)
    ? new Date(rawTimestamp * 1000)
    : null;
  return parsed && !Number.isNaN(parsed.getTime()) ? parsed : null;
};

const getLocalDateKey = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate()
  ).padStart(2, "0")}`;

const formatEventTime = (date, fallback) =>
  date
    ? date.toLocaleTimeString("fa-IR", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })
    : fallback || "-";

const DashboardPage = () => {
  const navigate = useNavigate();
  const [news, setNews] = useState([]);
  const [calendarDay, setCalendarDay] = useState("today");
  const [calendarState, setCalendarState] = useState(() => {
    const cachedPayload = readCalendarCache();
    return {
      payload: cachedPayload,
      loading: !cachedPayload,
      error: "",
    };
  });
  const [challenges, setChallenges] = useState([]);
  const [accountError, setAccountError] = useState("");

  const fetchCalendar = useCallback(async (signal) => {
    setCalendarState((current) => ({
      ...current,
      loading: !current.payload,
      error: "",
    }));
    try {
      const data = await getEconomicCalendar({ signal });
      const payload = normalizeCalendarPayload(data);
      writeCalendarCache(payload);
      setCalendarState({ payload, loading: false, error: "" });
    } catch (err) {
      if (signal?.aborted) return;
      console.error("calendar fetch error", err);
      setCalendarState((current) => ({
        ...current,
        loading: false,
        error: "دریافت تقویم اقتصادی با مشکل روبه‌رو شد.",
      }));
    }
  }, []);

  useEffect(() => {
    const calendarController = new AbortController();
    const fetchNews = async () => {
      try {
        const res = await fetch(WP_NEWS_URL);
        const data = await res.json();
        const normalized = (data || []).slice(0, 3).map((item) => {
          const image = item?._embedded?.["wp:featuredmedia"]?.[0]?.source_url || "/images/dashboard/news-1.png";
          return {
            id: item.id,
            title: item?.title?.rendered ? stripHtml(item.title.rendered) : "خبر اقتصادی",
            excerpt: item?.excerpt?.rendered ? stripHtml(item.excerpt.rendered) : stripHtml(item?.content?.rendered),
            image,
            link: item?.link,
          };
        });
        setNews(normalized);
      } catch (err) {
        console.error("news fetch error", err);
      }
    };

    fetchNews();
    fetchCalendar(calendarController.signal);
    const fetchChallenges = async () => {
      try {
        const data = await getAccountChallenges();
        const allAccounts = Array.isArray(data)
          ? data
          : Object.values(data || {}).filter(Array.isArray).flat();
        const uniqueAccounts = Array.from(
          new Map(allAccounts.map((account) => [account.id || account.account_number, account])).values()
        );
        setChallenges(uniqueAccounts);
      } catch (err) {
        console.error("challenge fetch error", err);
        setAccountError("خطا در دریافت چالش های فعال");
      }
    };
    fetchChallenges();
    return () => calendarController.abort();
  }, [fetchCalendar]);

  const selectedCalendarDate = useMemo(() => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    if (calendarDay === "tomorrow") date.setDate(date.getDate() + 1);
    return date;
  }, [calendarDay]);

  const selectedCalendarDateLabel = useMemo(
    () =>
      selectedCalendarDate.toLocaleDateString("fa-IR", {
        day: "numeric",
        month: "long",
      }),
    [selectedCalendarDate]
  );

  const events = useMemo(() => {
    const targetDateKey = getLocalDateKey(selectedCalendarDate);
    return (calendarState.payload?.events || [])
      .map((event, index) => {
        const date = getEventDate(event);
        return {
          id: event?.id || `${targetDateKey}-${index}`,
          date,
          time: formatEventTime(date, event?.time),
          currency: event?.country || event?.currency || "-",
          title: event?.title || event?.event || "رویداد مهم اقتصادی",
          impact: event?.impact || "High",
          forecast: event?.forecast || event?.estimate || "-",
          previous: event?.previous || event?.revised || "-",
        };
      })
      .filter((event) => event.date && getLocalDateKey(event.date) === targetDateKey)
      .sort((first, second) => first.date.getTime() - second.date.getTime());
  }, [calendarState.payload, selectedCalendarDate]);

  const highImpactOnly =
    String(calendarState.payload?.impact_filter || "high").toLowerCase() === "high";
  const selectedDayTitle = calendarDay === "today" ? "امروز" : "فردا";
  const emptyCalendarMessage = highImpactOnly
    ? `برای ${selectedDayTitle} رویداد مهمی نیست`
    : `برای ${selectedDayTitle} رویدادی نیست`;

  const accountCards = useMemo(
    () =>
      challenges.map((ch) => ({
        accountNumber: ch.account_number,
        items: [
          { id: "num", label: "Account Number", value: ch.account_number || "-", icon: "/images/icons/account-number.svg" },
          { id: "plan", label: "Plan", value: ch.plan_name || "-", icon: "/images/icons/account-type.svg" },
          { id: "balance", label: "Balance", value: ch.balance ?? "-", icon: "/images/icons/account-equity.svg" },
          { id: "equity", label: "Equity", value: ch.equti ?? "-", icon: "/images/icons/account-equity.svg" },
          { id: "profit", label: "Profit", value: ch.profit ?? "-", icon: "/images/icons/account-status.svg" },
          { id: "status", label: "Status", value: ch.challenge_status || "-", icon: "/images/icons/account-status.svg" },
        ],
      })),
    [challenges]
  );

  return (
    <MenuLayout>
      <section className="dash-header">
        <h1 className="dash-title">داشبورد</h1>
        <p className="dash-subtitle">
          اینجا خلاصه وضعیت حساب، خبرها و رویدادهای مهم بازار را می‌بینی.
        </p>
      </section>

      <section className="dash-quick-actions">
        <div className="dash-action-card" onClick={() => navigate("/competitions")}>
          <div className="dash-action-icon">
            <img
              src="/images/icons/qa-new-challenge.svg"
              alt="مسابقات"
              className="dash-action-icon-img"
            />
          </div>
          <div className="dash-action-text">
            <div className="dash-action-title">مسابقات</div>
            <div className="dash-action-subtitle">رقابت کن و جایزه بگیر؛ نتایج هر هفته به‌روز می‌شود.</div>
          </div>
        </div>

        <div className="dash-action-card dash-action-card-highlight" onClick={() => navigate("/plans")}>
          <div className="dash-action-icon">
            <img
              src="/images/icons/qa-fundex-plans.svg"
              alt="خرید چالش جدید"
              className="dash-action-icon-img"
            />
          </div>
          <div className="dash-action-text">
            <div className="dash-action-title">خرید چالش جدید</div>
            <div className="dash-action-subtitle">پلن مناسب خودت را انتخاب کن و شروع کن.</div>
          </div>
        </div>

        <div className="dash-action-card" onClick={() => window.open("https://touchprop.com/newrules/", "_blank", "noopener")}>
          <div className="dash-action-icon">
            <img
              src="/images/icons/qa-challenge-rules.svg"
              alt="قوانین چالش"
              className="dash-action-icon-img"
            />
          </div>
          <div className="dash-action-text">
            <div className="dash-action-title">قوانین چالش</div>
            <div className="dash-action-subtitle">قوانین را مرور کن تا آماده شروع باشی.</div>
          </div>
        </div>

        <div className="dash-action-card" onClick={() => navigate("/tickets")}>
          <div className="dash-action-icon">
            <img
              src="/images/icons/qa-open-ticket.svg"
              alt="ثبت تیکت"
              className="dash-action-icon-img"
            />
          </div>
          <div className="dash-action-text">
            <div className="dash-action-title">ثبت تیکت</div>
            <div className="dash-action-subtitle">سوال یا مشکلت را برای پشتیبانی بفرست.</div>
          </div>
        </div>
      </section>

      <section className="dash-content-row">
        <div className="dash-card dash-news-card">
          <div className="dash-news-header">
            <div className="dash-news-title-bar">
              <span className="dash-news-title-text">آخرین اخبار اقتصادی</span>
            </div>
          </div>

          <div className="dash-news-list">
            {news.map((n) => (
              <div
                className="dash-news-item news-clickable"
                key={n.id}
                role="button"
                title="برای مشاهده خبر کلیک کنید"
                onClick={() => n.link && window.open(n.link, "_blank", "noopener")}
              >
                <div className="dash-news-text">
                  <div className="dash-news-item-title">{n.title}</div>
                  <div className="dash-news-meta">
                    <div className="dash-news-meta-row">
                      <span className="dash-news-meta-label">خلاصه:</span>
                      <span className="dash-news-meta-value">{n.excerpt}</span>
                    </div>
                  </div>
                </div>

                <div className="dash-news-image-wrap">
                  <img src={n.image} alt={n.title} className="dash-news-image" />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="dash-card dash-trades-card dash-calendar-card">
          <div className="dash-calendar-toolbar">
            <div className="dash-calendar-heading">
              <div className="dash-calendar-title-row">
                <span className="dash-calendar-title">تقویم اقتصادی</span>
                <span className="dash-calendar-impact-note">
                  <i aria-hidden="true" /> فقط رویدادهای مهم
                </span>
              </div>
              <span className="dash-calendar-timezone">
                زمان‌ها بر اساس ساعت دستگاه شما نمایش داده می‌شوند
              </span>
            </div>

            <div className="dash-calendar-tabs" role="tablist" aria-label="انتخاب روز تقویم">
              <button
                type="button"
                role="tab"
                aria-selected={calendarDay === "today"}
                className={calendarDay === "today" ? "is-active" : ""}
                onClick={() => setCalendarDay("today")}
              >
                امروز
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={calendarDay === "tomorrow"}
                className={calendarDay === "tomorrow" ? "is-active" : ""}
                onClick={() => setCalendarDay("tomorrow")}
              >
                فردا
              </button>
            </div>
          </div>

          <div className="dash-calendar-dayline">
            <span>{selectedDayTitle}</span>
            <span>{selectedCalendarDateLabel}</span>
            {(calendarState.payload?.stale || (calendarState.error && calendarState.payload)) && (
              <span className="dash-calendar-stale">نمایش آخرین اطلاعات ذخیره‌شده</span>
            )}
          </div>

          <div className="dash-trades-table">
            {!calendarState.loading && events.length > 0 && (
              <>
                <div className="dash-trades-head">
                  <span>زمان</span>
                  <span>کشور</span>
                  <span>رویداد</span>
                  <span>اهمیت</span>
                  <span>پیش‌بینی</span>
                  <span>قبلی</span>
                </div>
                <div className="dash-trades-body">
                  {events.map((event) => (
                    <div className="dash-trades-row" key={event.id}>
                      <span className="dash-trades-time" data-label="زمان">{event.time}</span>
                      <span className="dash-trades-currency" data-label="کشور" dir="ltr">{event.currency}</span>
                      <span className="dash-trades-status" data-label="رویداد" dir="auto">{event.title}</span>
                      <span className="dash-trades-pnl" data-label="اهمیت">
                        <span className="dash-calendar-impact"><i aria-hidden="true" />مهم</span>
                      </span>
                      <span className="dash-trades-support" data-label="پیش‌بینی" dir="ltr">{event.forecast}</span>
                      <span className="dash-trades-action" data-label="قبلی" dir="ltr">{event.previous}</span>
                    </div>
                  ))}
                </div>
              </>
            )}

            {calendarState.loading && (
              <div className="dash-calendar-skeleton" aria-label="در حال دریافت رویدادها">
                {[0, 1, 2].map((item) => <span key={item} />)}
              </div>
            )}

            {!calendarState.loading && calendarState.error && !calendarState.payload && (
              <div className="dash-calendar-state dash-calendar-state-error" role="alert">
                <span className="dash-calendar-state-icon" aria-hidden="true">!</span>
                <strong>تقویم فعلاً در دسترس نیست</strong>
                <span>اتصال به منبع رویدادها برقرار نشد.</span>
                <button type="button" onClick={() => fetchCalendar()}>تلاش دوباره</button>
              </div>
            )}

            {!calendarState.loading && calendarState.payload && events.length === 0 && (
              <div className="dash-calendar-state" role="status">
                <span className="dash-calendar-state-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none">
                    <path d="M7 3v3M17 3v3M4 9h16M5 5h14a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1Z" />
                    <path d="m9 14 2 2 4-4" />
                  </svg>
                </span>
                <strong>{emptyCalendarMessage}</strong>
                <span>
                  {highImpactOnly
                    ? "در این بخش فقط رویدادهای High Impact فارکس فکتوری نمایش داده می‌شوند."
                    : "رویدادی برای این روز در تقویم ثبت نشده است."}
                </span>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="dash-accounts-section">
        {accountError && (
          <div className="dash-account-empty">
            خطا در دریافت چالش های فعال
          </div>
        )}
        {!accountError && accountCards.length === 0 && (
          <div className="dash-account-empty">
            چالش فعالی ندارید
          </div>
        )}
        {accountCards.map((block, idx) => (
          <div className="dash-account-block" key={idx}>
            <div className="dash-account-cards-row">
              {block.items.map((acc) => (
                <div className="dash-account-mini-card" key={`${idx}-${acc.id}`}>
                  <div className="dash-account-mini-icon">
                    <img src={acc.icon} alt={acc.label} />
                  </div>
                  <div className="dash-account-mini-label">{acc.label}</div>
                  <div className="dash-account-mini-value">{acc.value}</div>
                </div>
              ))}
            </div>
            <button className="dash-account-analyze-btn" disabled={!block.accountNumber} onClick={() => block.accountNumber && navigate(`/analysis/${block.accountNumber}`)}>
              تحلیل حساب
            </button>
          </div>
        ))}
      </section>
    </MenuLayout>
  );
};

export default DashboardPage;
